import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  FileText, Edit, Copy, Eye, Search, Filter,
  Clock, Plus, Archive, Printer, Code,
  TrendingUp, Layers, Share2, ChevronDown,
  Download, Power, Trash2, Stamp
} from 'lucide-react';
import {
  TEMPLATE_CATEGORIES, TEMPLATE_STATUS, RECIPIENTS,
  getCategoryInfo
} from './constants';

export default function TemplatesTab({
  filteredTemplates,
  searchTerm, setSearchTerm,
  selectedCategory, setSelectedCategory,
  selectedStatus, setSelectedStatus,
  sortBy, setSortBy,
  sortOrder, setSortOrder,
  stamps,
  openEditor,
  exportToPDF, exportToHTML, printTemplate,
  changeStatus, deleteTemplate,
  setSelectedTemplate, setShowPreviewModal, setShowShareModal
}) {
  return (
    <div className="space-y-4">
      {/* Filtres avancés */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher un modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="template-search-input"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <Layers className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className="flex items-center gap-2">
                      <cat.icon className="h-4 w-4" />
                      {cat.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(TEMPLATE_STATUS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <value.icon className="h-4 w-4" />
                      {value.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="updatedAt">Date modification</SelectItem>
                <SelectItem value="name">Nom</SelectItem>
                <SelectItem value="usageCount">Utilisation</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des modèles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-grid">
        {filteredTemplates.map((template) => {
          const catInfo = getCategoryInfo(template.category);
          const statusInfo = TEMPLATE_STATUS[template.status] || TEMPLATE_STATUS.draft;
          
          return (
            <Card key={template.id} className="hover:shadow-lg transition-all group" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${catInfo.color} flex items-center justify-center`}>
                      <catInfo.icon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base line-clamp-1">{template.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${catInfo.color} text-white text-xs`}>
                          {catInfo.label}
                        </Badge>
                        <Badge className={`${statusInfo.color} text-white text-xs`}>
                          {statusInfo.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                  {template.description || 'Aucune description'}
                </p>
                
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {template.updatedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {template.usageCount} util.
                  </span>
                </div>
                
                {template.sharedWith?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.sharedWith.map(r => {
                      const recipient = RECIPIENTS.find(rec => rec.id === r);
                      return recipient ? (
                        <Badge key={r} variant="outline" className="text-xs">
                          <recipient.icon className="h-3 w-3 mr-1" />
                          {recipient.label}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                
                {template.stampId && (
                  <div className="flex items-center gap-2 mb-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                    <Stamp className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-semibold text-black">
                      {(() => {
                        const stamp = stamps.find(s => s.id === template.stampId) || 
                                     (typeof template.stampId === 'number' && stamps[template.stampId - 1]);
                        return stamp ? stamp.name : 'Tampon non trouvé';
                      })()}
                    </span>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1 pt-2 border-t">
                  <Button size="sm" variant="ghost" title="Voir"
                    onClick={() => { setSelectedTemplate(template); setShowPreviewModal(true); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Modifier"
                    onClick={() => openEditor(template, 'edit')}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Dupliquer"
                    onClick={() => openEditor(template, 'duplicate')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="PDF"
                    onClick={() => exportToPDF(template)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="HTML"
                    onClick={() => exportToHTML(template)}>
                    <Code className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Imprimer"
                    onClick={() => printTemplate(template)}>
                    <Printer className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Partager"
                    onClick={() => { setSelectedTemplate(template); setShowShareModal(true); }}>
                    <Share2 className="h-4 w-4" />
                  </Button>
                  {template.status === 'active' ? (
                    <Button size="sm" variant="ghost" title="Archiver"
                      onClick={() => changeStatus(template.id, 'archived')}>
                      <Archive className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" title="Activer" className="text-green-600"
                      onClick={() => changeStatus(template.id, 'active')}>
                      <Power className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-red-600" title="Supprimer"
                    onClick={() => deleteTemplate(template.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Aucun modèle trouvé</p>
            <Button className="mt-4" onClick={() => openEditor(null, 'create')}>
              <Plus className="h-4 w-4 mr-2" />
              Créer un modèle
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
